import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { CalendarEvent } from '@demind-inc/core';

import { selectedDateAtom } from '../recoil';
import { useCalendarContext, useCircadianContext } from '../context';
import { useEventActions } from './useEventActions';
import { useTaskActions } from './useTaskActions';
import { useTodoTasksCache } from '../queries';
import { RBCEvent, transformRBCEventToCalendarEvent } from '../../components';
import { trackEventMixpanel } from '../../utils';

interface UseEventBlockProps {
  onRemoveTemporalSuggestedEvent: (eventId: string) => void;
}
export const useEventBlock = ({ onRemoveTemporalSuggestedEvent }: UseEventBlockProps) => {
  const { updateTodoTaskStatus } = useTaskActions();
  const { findInCacheByDate } = useTodoTasksCache();
  const { createCalendarEvent, updateCalendarEvent } = useEventActions();
  const { findPhaseForTaskTime } = useCircadianContext();
  const { calendarPreferences } = useCalendarContext();

  const selectedDate = useRecoilValue(selectedDateAtom);

  const handleTaskComplete = useCallback(
    async (taskId: string, boardId: string, completed: boolean, originalTaskId?: string) => {
      await updateTodoTaskStatus({
        taskId,
        boardId,
        completed,
        originalTaskId,
        mutateParams: {
          date: dayjs(selectedDate).format('YYYY-MM-DD'),
        },
      });
    },
    [updateTodoTaskStatus, selectedDate]
  );

  const getTargetTaskFromEvent = useCallback(
    (event: RBCEvent) => {
      const taskId = event.taskFrom?.taskId;
      if (!taskId) {
        return;
      }

      const tasks = findInCacheByDate(
        dayjs(event.start).format('YYYY-MM-DD'),
        event.taskFrom.boardId
      );

      return tasks?.find((task) => task.taskId === taskId);
    },
    [findInCacheByDate]
  );

  const shouldShowTaskEvent = useCallback(
    (event: RBCEvent) => {
      // Be strict about boolean-ness: backend/preferences can sometimes hydrate as strings.
      // Only hide when it's explicitly enabled.
      if (calendarPreferences.hideAutoSchedule?.completedTask !== true) {
        return true;
      }

      const linkedTask = getTargetTaskFromEvent(event);
      if (!linkedTask) {
        return true;
      }
      return !linkedTask.completed;
    },
    [calendarPreferences.hideAutoSchedule?.completedTask, getTargetTaskFromEvent]
  );

  const handleAcceptSuggestedTaskEvent = useCallback(
    async (event: Partial<RBCEvent>, calendarId: string) => {
      const startIso = dayjs(event.start).toISOString();
      const endIso = dayjs(event.end).toISOString();

      const calendarEvent = transformRBCEventToCalendarEvent(event as RBCEvent);
      const newEvent: Partial<CalendarEvent> = {
        ...calendarEvent,
        autoScheduleFrom: {
          ...calendarEvent.autoScheduleFrom!,
          predicted: false,
        },
        start: {
          date: startIso,
          timeZone: dayjs.tz.guess(),
        },
        end: {
          date: endIso,
          timeZone: dayjs.tz.guess(),
        },
      };

      onRemoveTemporalSuggestedEvent(calendarEvent.eventId);
      const newEventId = uuidv4().replace(/-/g, '');

      createCalendarEvent({
        calendarId: calendarId,
        newEventOption: {
          ...newEvent,
          eventId: newEventId,
        },
      });

      trackEventMixpanel('task_time_auto_suggested_accepted');
    },
    [
      createCalendarEvent,
      findPhaseForTaskTime,
      onRemoveTemporalSuggestedEvent,
      getTargetTaskFromEvent,
    ]
  );

  const handleDiscardSuggestedTaskEvent = useCallback(
    async (eventId: string) => {
      onRemoveTemporalSuggestedEvent(eventId);
      trackEventMixpanel('task_time_auto_suggested_discarded');
    },
    [onRemoveTemporalSuggestedEvent]
  );

  const handleRoutineComplete = useCallback(
    (event: RBCEvent, completed: boolean) => {
      if (!event.eventId || !event.calendarId || !event.routineFrom) {
        return;
      }

      updateCalendarEvent({
        eventId: event.eventId,
        calendarId: event.calendarId,
        newEventOption: {
          routineFrom: {
            ...event.routineFrom,
            completed,
          },
        },
      });
    },
    [updateCalendarEvent]
  );

  return {
    shouldShowTaskEvent,
    handleTaskComplete,
    handleRoutineComplete,
    getTargetTaskFromEvent,
    handleAcceptSuggestedTaskEvent,
    handleDiscardSuggestedTaskEvent,
  };
};
